import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { useBulkCreateChannelInvoices } from "../hooks/useBulkCreateChannelInvoices";
import { useChannel } from "../hooks/useChannel";
import { useChannelMonth } from "../hooks/useChannelMonth";
import { InvoiceChannelTableToolbar } from "./components/InvoiceChannelTableToolbar";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import {
  EditableDataTable,
  EditableDataTableRef,
} from "@/components/ui/v2/table/editable-data-table";
import Tabs from "@/components/ui/v2/tabs";
import { ROUTES } from "@/constants/routes";
import { parseAmount } from "@/lib/helpers/common";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useDateConstraints } from "@/lib/hooks/useDateConstraints";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { useChannelMonthTableColumns } from "@/lib/tableColumns/useChannelMonthTableColumns";
import { isYearMonthInFuture } from "@/lib/utils/dateUtils";
import { ChannelMonthType } from "@/types/response/ChannelMonthType";
import { SortDirection, TableColumn } from "@/types/table/tableProps";

type InvoiceChannelPageContentProps = {
  currentPageLabel?: string;
  descending: boolean | undefined;
  handleSort: (
    accessor: TableColumn<ChannelMonthType>["accessor"],
    direction: SortDirection | null,
  ) => void;
  channelId: string;
  month: string;
  orderBy?: string;
  page: number;
  pageSize: number;
  search?: string;
  searchValue?: string;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSearch: (search: string) => void;
  year: string;
  tenantId: string | null;
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  onEditModeReset: () => void;
};

const InvoiceChannelPageContent = ({
  currentPageLabel,
  descending,
  handleSort,
  channelId,
  month,
  orderBy,
  page,
  pageSize,
  search,
  searchValue,
  setPage,
  setPageSize,
  setSearch,
  year,
  tenantId,
  isEditMode,
  setIsEditMode,
  onEditModeReset,
}: InvoiceChannelPageContentProps) => {
  const bulkCreateInvoice = useBulkCreateChannelInvoices({
    channelId,
    year,
    month,
    tenantId,
  });
  const location = useLocation();
  const getSelectedTabId = () => {
    const currentPath = location.pathname;
    if (currentPath.includes(`/${ROUTES.toDo}`)) {
      return "toDo";
    }
    if (currentPath.includes(`/${ROUTES.invoiced}`)) {
      return "invoiced";
    }
    return "toDo";
  };
  const selectedTabId = getSelectedTabId();
  const isInvoiced = selectedTabId === "invoiced";
  const {
    data: channelMonths,
    isLoading,
    isFetching,
  } = useChannelMonth({
    channelId,
    year,
    month,
    descending,
    orderBy,
    page,
    pageSize,
    search,
    tenantId,
    isInvoiced,
  });

  const { channelMonthTableColumns } = useChannelMonthTableColumns();
  const { t } = useTranslation();
  const { data: channel } = useChannel({
    channelId,
  });
  const { maxDate } = useDateConstraints();
  const editableTableRef = useRef<EditableDataTableRef<ChannelMonthType>>(null);

  // Auto-navigate to previous page if current page becomes empty after invoice creation
  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      channelMonths?.data &&
      channelMonths.data.length === 0 &&
      page > 1 &&
      channelMonths.totalRecords !== undefined
    ) {
      // Calculate the last page with data
      const totalPages = Math.ceil(channelMonths.totalRecords / pageSize);
      const targetPage = Math.max(1, totalPages);
      if (targetPage !== page) {
        setPage(targetPage);
      }
    }
  }, [
    channelMonths?.data,
    channelMonths?.totalRecords,
    isLoading,
    isFetching,
    page,
    pageSize,
    setPage,
  ]);

  const handleUpdateData = async (newData: ChannelMonthType[]) => {
    const invoices = newData
      .filter((item) => item.spent !== null && item.spent !== undefined)
      .map((item) => {
        const amount = parseAmount(item.spent);
        return {
          amount: amount,
          clientId2: item.clientId2,
        };
      });
    if (invoices.length) {
      await bulkCreateInvoice.mutateAsync({
        invoices,
      });
    }
  };

  const handleSaveChanges = async () => {
    const editedData = editableTableRef.current?.getEditedData() ?? [];
    await handleUpdateData(editedData);
    // Edit mode will be reset after mutation succeeds and data refreshes
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleEditModeChange = (newEditMode: boolean) => {
    setIsEditMode(newEditMode);
  };

  const tabs = [
    {
      id: "toDo",
      label: t("common.toDo"),
      href: `/${ROUTES.invoices}/${ROUTES.channels}/${channelId}/${ROUTES.toDo}/${year}/${month}`,
      content: <></>,
    },
    {
      id: "invoiced",
      label: t("common.invoiced"),
      href: `/${ROUTES.invoices}/${ROUTES.channels}/${channelId}/${ROUTES.invoiced}/${year}/${month}`,
      content: <></>,
    },
  ];

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.invoices"), href: `/${ROUTES.invoices}` },
      {
        label: t("common.channels"),
        href: `/${ROUTES.invoices}/${ROUTES.channels}`,
      },
      {
        label: currentPageLabel ?? channel?.name ?? t("common.channel"),
      },
    ],
  });

  return (
    <ModernPageLayout
      backbutton={{
        href: `../../`,
        label: t("common.back"),
      }}
      breadcrumbs={breadcrumbs}
      title={`${t("common.invoices")} ${t("common.for")} ${
        currentPageLabel ?? channel?.name ?? "..."
      }`}
    >
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between w-full">
          <Tabs tabs={tabs} defaultTabId={getSelectedTabId()} />
        </div>

        <InvoiceChannelTableToolbar
          onSearchChange={setSearch}
          searchValue={searchValue}
          year={year}
          month={month}
          channelId={channelId}
          maxDate={maxDate}
          currentPageLabel={currentPageLabel}
          channelName={channel?.name}
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          isEditDisabled={isYearMonthInFuture([year, month])}
          isSaving={bulkCreateInvoice.isPending}
          hasData={(channelMonths?.data?.length ?? 0) > 0}
          onEditModeReset={onEditModeReset}
        />

        <EditableDataTable<ChannelMonthType>
          ref={editableTableRef}
          columns={channelMonthTableColumns}
          data={channelMonths?.data ?? []}
          descending={descending}
          editableFields={["spent"]}
          entityName={"Invoice"}
          getRowId={(row) => row.clientId2}
          isEditDisabled={isYearMonthInFuture([year, month])}
          isEditing={bulkCreateInvoice.isPending}
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          isLoading={isLoading || isFetching}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          onUpdateData={handleUpdateData}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          orderBy={orderBy}
          page={page}
          pageSize={pageSize}
          totalRecords={channelMonths?.totalRecords ?? 0}
        />
      </div>
    </ModernPageLayout>
  );
};

const InvoiceChannelPage = () => {
  const { channelId, year, month } = useParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler<ChannelMonthType>();
  const { page, setPage: setPageBase } = usePageSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { pageSize, setPageSize: setPageSizeBase } = usePageSizeSearchParams();
  const { tenantId } = useAuth();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);

  // Reset edit mode when month/year changes
  useEffect(() => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const setPage = (newPage: number) => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    setPageBase(newPage);
  };

  const setPageSize = (newPageSize: number) => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    setPageSizeBase(newPageSize);
  };

  const setSearch = (newSearch: string) => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    setSearchValue(newSearch);
  };

  const handleEditModeReset = () => {
    setIsEditMode(false);
  };

  if (!channelId || !month || !year) return null;

  return (
    <InvoiceChannelPageContent
      currentPageLabel={location.state?.channelName}
      descending={descending}
      handleSort={handleSort}
      channelId={channelId}
      month={month}
      orderBy={orderBy}
      page={page}
      pageSize={pageSize}
      search={debouncedSearchValue}
      searchValue={searchValue}
      setPage={setPage}
      setPageSize={setPageSize}
      setSearch={setSearch}
      year={year}
      tenantId={tenantId}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      onEditModeReset={handleEditModeReset}
    />
  );
};

export default InvoiceChannelPage;
